import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SearchIcon from '@images/svg-icons/searchIcon.svg';
import { IMappedContactLensItem } from '@interfaces/myAppointmentInterfaces';
import { searchContactLenses } from '@utils/pages-utils/MyProfileUtils';
import { getLabelFallback } from '@utils/utils';
import { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { RootState } from 'src/store/reducers';
import CloseIcon from '@images/svg-icons/closeIconSelectBox.svg';

type SelectBoxCLProps = {
    isValid: boolean;
    selectedItem?: IMappedContactLensItem;
    onSelectCL: (element: any) => void;
};

const SelectBoxCL = (props: SelectBoxCLProps): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);

    const [selectedOption, setSelectedOption] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [hasFocus, setHasFocus] = useState(false);
    const [inputValue, setInputValue] = useState(''); // Stato per tracciare il testo scritto

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '48px',
            height: '48px',
            borderColor: state.isFocused ? '#ddd' : props.isValid ? '#ddd' : 'red',
            // overwrittes hover style
            '&:hover': {
                borderColor: state.isFocused ? '#ddd' : props.isValid ? '#ddd' : 'red',
            },
        }),

        valueContainer: (provided, state) => ({
            ...provided,
            height: '48px',
            marginLeft: hasFocus ? '52px' : 'inherit',
        }),

        input: (provided, state) => ({
            ...provided,
            margin: '0px',
        }),

        indicatorSeparator: state => ({
            display: 'none',
        }),

        indicatorsContainer: (provided, state) => ({
            ...provided,
            height: '48px',
        }),

        menu: (provided, state) => ({
            ...provided,
            margin: '0px',
        }),
    };

    const loadOptions = (inputValue: string, callback: (options) => void) => {
        if (!!searchTimeout) {
            // If input change event was already in progress, clear the previous event
            clearTimeout(searchTimeout);
            setSearchTimeout(searchTimeout);
        }

        setSearchTimeout(
            setTimeout(async () => {
                // Need to wait the contact lenses search service before updating the select box options
                const mappedContactLensList = await searchContactLenses(
                    config.mockRequest,
                    config.storeId,
                    config.catalogIdCAS,
                    config.categoryIdCl,
                    inputValue
                );

                let options = [];

                // Create the option elements
                for (const mappedContactLensItem of mappedContactLensList) {
                    const option = createOption(mappedContactLensItem, inputValue);

                    options.push(option);
                }

                // Callback for select box
                callback(options);
            }, 1000)
        );
    };

    const createOption = (element: IMappedContactLensItem, inputValue?: string) => {
        let productNameElem = element?.productName;

        if (!!productNameElem && !!inputValue) {
            productNameElem = productNameElem.replace(
                new RegExp(inputValue, 'gi'),
                "<span class='select-cl-label-black'>" + inputValue + '</span>'
            );
        }

        return {
            value: element,
            label: (
                <div className="select-cl-label">
                    <LazyLoadImage
                        className="select-cl-label-src"
                        alt={element.productName}
                        src={element.productImageUrl}
                        max-height="30px"
                        max-width="30px"
                    />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: productNameElem,
                        }}
                    ></span>
                </div>
            ),
        };
    };

    const onChange = e => {
        setSelectedOption(e);
        setHasFocus(false);

        props.onSelectCL(e?.value);
    };

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);
    };

    const handleClearInput = () => {
        setInputValue('');
        setSelectedOption(null);
        props.onSelectCL(null);
    };

    const onFocus = e => {
        setHasFocus(true);
    };

    const onBlur = e => {
        setHasFocus(false);
    };

    return (
        <div className="selectbox-cl">
            {!props.isValid && (
                <div className={`error-icon-container`}>
                    <ErrorIcon />
                </div>
            )}
            {hasFocus && (
                <>
                    <div className={`search-icon-container ${!!props.selectedItem ? ' search-icon-container-selected-option' : !selectedOption ? '' : ' search-icon-container-selected-option'} ${!props.isValid ? ' is-not-valid' : ''}`}>
                        <SearchIcon />
                    </div>
                    <div className={`placeholder-focused ${!props.isValid ? ' is-not-valid' : ''}`}>
                        {getLabelFallback('ADD_PRESCRIPTION_STEP_1_SELECTBOX_PLACEHOLDER')}
                    </div>
                    {inputValue && (
                        <div className="close-icon-container" onClick={handleClearInput}>
                            <CloseIcon />
                        </div>
                    )}
                </>
            )}

            <AsyncSelect
                cacheOptions
                value={
                    !!props.selectedItem && !selectedOption
                        ? createOption(props.selectedItem)
                        : selectedOption
                }
                loadOptions={loadOptions}
                placeholder={
                    !hasFocus
                        ? getLabelFallback('ADD_PRESCRIPTION_STEP_1_SELECTBOX_PLACEHOLDER')
                        : ''
                }
                className="selectbox-cl-inner"
                classNamePrefix={`${!!props.selectedItem ? 'selected-option' : !selectedOption ? '' : 'selected-option'} selectbox-cl-prefix`}
                onChange={onChange}
                onInputChange={handleInputChange}
                onFocus={onFocus}
                onBlur={onBlur}
                styles={customStyles}
            />
            {!props.isValid && (
                <div className="selectbox-cl-error-message">Please select a brand to proceed</div>
            )}
        </div>
    );
};

export default SelectBoxCL;
